import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { signOut } from 'firebase/auth';
import { auth, db, storage } from '../firebase';
import './FeedbackForm.css';

function FeedbackForm() {
  const location = useLocation();
  const navigate = useNavigate();
  const user = auth.currentUser;

  // Get product data from navigation state
  const productData = location.state || {};
  const { productId, productName, applicationId } = productData;

  const [formData, setFormData] = useState({
    designRating: 3,
    functionalityRating: 3,
    performanceRating: 3,
    valueRating: 3,
    overallRating: 3,
    mostUsedFeatures: '',
    leastUsedFeatures: '',
    issues: '',
    suggestions: ''
  });
  const [imageFiles, setImageFiles] = useState([]); // Array of files
  const [imagePreviews, setImagePreviews] = useState([]); // Array of preview URLs
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);

  // Redirect if no product data
  useEffect(() => {
    if (!productId || !productName || !applicationId) {
      alert('Invalid access. Please submit feedback from your approved applications.');
      navigate('/my-applications');
    }
  }, [productId, productName, applicationId, navigate]);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    
    if (files.length === 0) return;

    // Check if adding these files would exceed 5 total images
    if (imageFiles.length + files.length > 5) {
      alert('Maximum 5 images allowed. You can upload ' + (5 - imageFiles.length) + ' more.');
      return;
    }

    // Validate each file
    const validFiles = [];
    const newPreviews = [];

    for (let file of files) {
      // Check file size (max 5MB per image)
      if (file.size > 5 * 1024 * 1024) {
        alert(`${file.name} is too large. Maximum size is 5MB per image.`);
        continue;
      }

      // Check file type
      if (!file.type.startsWith('image/')) {
        alert(`${file.name} is not an image file.`);
        continue;
      }

      validFiles.push(file);

      // Create preview
      const reader = new FileReader();
      reader.onloadend = () => {
        newPreviews.push(reader.result);
        if (newPreviews.length === validFiles.length) {
          setImagePreviews([...imagePreviews, ...newPreviews]);
        }
      };
      reader.readAsDataURL(file);
    }

    setImageFiles([...imageFiles, ...validFiles]);
  };

  const handleRemoveImage = (index) => {
    setImageFiles(imageFiles.filter((_, i) => i !== index));
    setImagePreviews(imagePreviews.filter((_, i) => i !== index));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validate all fields are filled
    if (!formData.issues.trim() || !formData.suggestions.trim() || !formData.mostUsedFeatures.trim() || !formData.leastUsedFeatures.trim()) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);

    try {
      const imageUrls = [];

      // Upload all images if any selected
      if (imageFiles.length > 0) {
        for (let i = 0; i < imageFiles.length; i++) {
          const file = imageFiles[i];
          const fileExtension = file.name.split('.').pop();
          const sanitizedEmail = user.email.replace(/[@.]/g, '_');
          const fileName = `${sanitizedEmail}_${Date.now()}_${i + 1}.${fileExtension}`;
          
          const imageRef = ref(storage, `feedback/${user.uid}/${fileName}`);
          await uploadBytes(imageRef, file);
          const url = await getDownloadURL(imageRef);
          imageUrls.push(url);
        }
      }

      await addDoc(collection(db, 'feedback'), {
        userId: user.uid,
        email: user.email,
        productId: productId,
        productName: productName,
        applicationId: applicationId,
        designRating: parseInt(formData.designRating),
        functionalityRating: parseInt(formData.functionalityRating),
        performanceRating: parseInt(formData.performanceRating),
        valueRating: parseInt(formData.valueRating),
        overallRating: parseInt(formData.overallRating),
        mostUsedFeatures: formData.mostUsedFeatures,
        leastUsedFeatures: formData.leastUsedFeatures,
        issues: formData.issues,
        suggestions: formData.suggestions,
        imageUrls: imageUrls, // Array of image URLs
        imageCount: imageUrls.length, // Number of images uploaded
        createdAt: serverTimestamp()
      });

      setSuccess(true);
      setTimeout(() => {
        navigate('/my-applications');
      }, 2000);
    } catch (error) {
      console.error('Error submitting feedback:', error);
      alert('Error submitting feedback. Please try again.');
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error('Error logging out:', error);
    }
  };

  if (success) {
    return (
      <div className="feedback-page">
        <nav className="nav">
          <div className="nav-container">
            <div className="logo">BetaBuyers</div>
            <button onClick={handleLogout} className="btn-logout">Sign Out</button>
          </div>
        </nav>
        <div className="form-content">
          <div className="success-message">
            <h2>✓ Feedback Submitted!</h2>
            <p>Thank you for your feedback on {productName}. Redirecting to your applications...</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="feedback-page">
      <nav className="nav">
        <div className="nav-container">
          <div className="logo">BetaBuyers</div>
          <div className="nav-buttons">
            <button onClick={() => navigate('/my-applications')} className="btn-nav">My Applications</button>
            <button onClick={handleLogout} className="btn-logout">Sign Out</button>
          </div>
        </div>
      </nav>

      <div className="form-content">
        <div className="form-header">
          <h1>Submit Feedback</h1>
          <p>Share your experience testing <strong>{productName}</strong></p>
        </div>

        <form onSubmit={handleSubmit} className="feedback-form">
          <div className="product-info-display">
            <p><strong>Product:</strong> {productName}</p>
            <p className="info-note">You're reviewing this product based on your approved beta testing application</p>
          </div>

          {/* Rating Section */}
          <div className="form-section-header">
            <h3>Rate Your Experience</h3>
          </div>

          <div className="form-group">
            <label htmlFor="designRating">Design & Aesthetics *</label>
            <div className="rating-slider-container">
              <div className="rating-labels">
                <span>Poor</span>
                <span>Excellent</span>
              </div>
              <input
                type="range"
                id="designRating"
                name="designRating"
                min="1"
                max="5"
                step="1"
                value={formData.designRating}
                onChange={handleChange}
                disabled={loading}
                required
                className="rating-slider"
              />
              <div className="rating-value">
                {formData.designRating} / 5 Stars
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="functionalityRating">Functionality & Features *</label>
            <div className="rating-slider-container">
              <div className="rating-labels">
                <span>Poor</span>
                <span>Excellent</span>
              </div>
              <input
                type="range"
                id="functionalityRating"
                name="functionalityRating"
                min="1"
                max="5"
                step="1"
                value={formData.functionalityRating}
                onChange={handleChange}
                disabled={loading}
                required
                className="rating-slider"
              />
              <div className="rating-value">
                {formData.functionalityRating} / 5 Stars
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="performanceRating">Performance & Reliability *</label>
            <div className="rating-slider-container">
              <div className="rating-labels">
                <span>Poor</span>
                <span>Excellent</span>
              </div>
              <input
                type="range"
                id="performanceRating"
                name="performanceRating"
                min="1"
                max="5"
                step="1"
                value={formData.performanceRating}
                onChange={handleChange}
                disabled={loading}
                required
                className="rating-slider"
              />
              <div className="rating-value">
                {formData.performanceRating} / 5 Stars
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="valueRating">Value for Money *</label>
            <div className="rating-slider-container">
              <div className="rating-labels">
                <span>Poor</span>
                <span>Excellent</span>
              </div>
              <input
                type="range"
                id="valueRating"
                name="valueRating"
                min="1"
                max="5"
                step="1"
                value={formData.valueRating}
                onChange={handleChange}
                disabled={loading}
                required
                className="rating-slider"
              />
              <div className="rating-value">
                {formData.valueRating} / 5 Stars
              </div>
            </div>
          </div>

          <div className="form-group">
            <label htmlFor="overallRating">Overall Rating *</label>
            <div className="rating-slider-container">
              <div className="rating-labels">
                <span>Poor</span>
                <span>Excellent</span>
              </div>
              <input
                type="range"
                id="overallRating"
                name="overallRating"
                min="1"
                max="5"
                step="1"
                value={formData.overallRating}
                onChange={handleChange}
                disabled={loading}
                required
                className="rating-slider"
              />
              <div className="rating-value overall">
                {formData.overallRating} / 5 Stars
              </div>
            </div>
          </div>

          {/* Feature Usage Section */}
          <div className="form-section-header">
            <h3>Feature Usage</h3>
          </div>

          <div className="form-group">
            <label htmlFor="mostUsedFeatures">Most Used Features *</label>
            <textarea
              id="mostUsedFeatures"
              name="mostUsedFeatures"
              value={formData.mostUsedFeatures}
              onChange={handleChange}
              required
              disabled={loading}
              rows="3"
              placeholder="Which features did you use the most and why?..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="leastUsedFeatures">Least Used Features *</label>
            <textarea
              id="leastUsedFeatures"
              name="leastUsedFeatures"
              value={formData.leastUsedFeatures}
              onChange={handleChange}
              required
              disabled={loading}
              rows="3"
              placeholder="Which features did you use the least or not at all? Why?..."
            />
          </div>

          {/* Detailed Feedback Section */}
          <div className="form-section-header">
            <h3>Detailed Feedback</h3>
          </div>

          <div className="form-group">
            <label htmlFor="issues">Issues Found *</label>
            <textarea
              id="issues"
              name="issues"
              value={formData.issues}
              onChange={handleChange}
              required
              disabled={loading}
              rows="4"
              placeholder="Describe any bugs, problems, or issues you encountered..."
            />
          </div>

          <div className="form-group">
            <label htmlFor="suggestions">Suggestions for Improvement *</label>
            <textarea
              id="suggestions"
              name="suggestions"
              value={formData.suggestions}
              onChange={handleChange}
              required
              disabled={loading}
              rows="4"
              placeholder="What features or improvements would you like to see?..."
            />
          </div>

          {/* IMAGE UPLOAD SECTION - MULTIPLE IMAGES */}
          <div className="form-group">
            <label>Upload Screenshots or Images (Optional)</label>
            <p className="field-hint">
              Upload up to 5 images. Max 5MB per image. {imageFiles.length > 0 && `(${imageFiles.length}/5 uploaded)`}
            </p>
            
            <div className="image-upload-container">
              {/* Show upload button if less than 5 images */}
              {imageFiles.length < 5 && (
                <label className="image-upload-label">
                  <input
                    type="file"
                    accept="image/*"
                    onChange={handleImageChange}
                    className="image-input-hidden"
                    disabled={loading}
                    multiple
                  />
                  <div className="upload-placeholder">
                    <span className="upload-icon">📷</span>
                    <span>Click to upload images ({5 - imageFiles.length} remaining)</span>
                  </div>
                </label>
              )}

              {/* Show all image previews */}
              {imagePreviews.length > 0 && (
                <div className="images-grid">
                  {imagePreviews.map((preview, index) => (
                    <div key={index} className="image-preview-container">
                      <img src={preview} alt={`Preview ${index + 1}`} className="image-preview" />
                      <button 
                        type="button" 
                        onClick={() => handleRemoveImage(index)} 
                        className="btn-remove-image"
                        disabled={loading}
                      >
                        ✕
                      </button>
                      <span className="image-number">{index + 1}</span>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <button type="submit" className="btn-submit-form" disabled={loading}>
            {loading ? 'Submitting...' : 'Submit Feedback'}
          </button>
        </form>
      </div>
    </div>
  );
}

export default FeedbackForm;